import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import PlusIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useState } from 'react';
import { useCustomFieldRules } from 'hooks/useCustomFieldRules';
import { AssignCustomFieldsDialog } from 'components/custom-fields/AssignCustomFieldsDialog';
import { customFieldsQueries } from 'services/custom-fields/customFieldsQueries';
import { useAssignCustomFieldMutation } from './useAssignCustomFieldMutation';
import { useUnassignCustomFieldMutation } from './useUnassignCustomFieldMutation';
import { AssignedCustomFieldsList } from './AssignedCustomFieldsList';
import type { CustomField } from 'services/custom-fields/schemas';
import SvgIcoPremiumIntegration from 'components/icon/icons/IcoPremiumIntegration';
import { SETTINGS_ROUTES } from 'routing/settingsRoutes';

type CustomFieldsSectionProps = {
  level: 'document' | 'template';
  id: string;
};

export const CustomFieldsSection = ({ level, id }: CustomFieldsSectionProps) => {
  const { t } = useTranslation();
  const rules = useCustomFieldRules({ level, contentId: id });
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const isTemplate = level === 'template';
  const listAssignedQuery = useMemo(() => customFieldsQueries.listEditorAssignedCustomFields(id, isTemplate), [id]);
  const listUnassignedQuery = useMemo(() => customFieldsQueries.listUnassignedCustomFields(id, isTemplate), [id]);

  const { mutate } = useAssignCustomFieldMutation(id, isTemplate);
  const { mutate: unassign } = useUnassignCustomFieldMutation(id, level);

  const onAssign = useCallback((field: CustomField) => mutate(field), [mutate]);

  if (!('needsUpgrade' in rules) || !('canEditValues' in rules) || !('canAssign' in rules))
    throw new Error('those rules should be have been evaluated for documents or templates');

  if (!rules.canShow) return null;

  return (
    <Box
      component="section"
      aria-labelledby="custom-fields-section-title"
      data-dd-action-name="Custom Fields Zone"
      sx={{ width: '100%', mt: 3, mb: 3 }}
    >
      <Typography
        id="custom-fields-section-title"
        sx={{ marginBottom: 1.5, marginTop: 2, fontSize: '14px' }}
        color="primary"
        align="left"
        variant="subtitle2"
        component="h3"
      >
        {t('editor.variables.custom_fields', 'Custom Fields')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {<AssignedCustomFieldsList canEdit={rules.canEditValues} query={listAssignedQuery} />}

        {rules.canAssign && (
          <Button
            sx={(theme) => ({
              svg: {
                color: rules.needsUpgrade ? theme.palette.warning.main : {},
              },
            })}
            variant="outlined"
            color="primary"
            fullWidth
            onClick={() => {
              if (rules.needsUpgrade) window.open('/' + SETTINGS_ROUTES.SETTINGS_PLAN.fullPath.join('/'), '_blank', 'noopener,noreferrer');
              else setIsDialogOpen(true);
            }}
            startIcon={rules.needsUpgrade ? <SvgIcoPremiumIntegration /> : <PlusIcon />}
            data-dd-action-name="Open Assign Custom Fields Dialog"
          >
            {t('editor.variables.assign_custom_fields', 'Assign Custom Fields')}
          </Button>
        )}
      </Box>
      <AssignCustomFieldsDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        level={level}
        listAssignedQuery={listAssignedQuery}
        listUnassignedQuery={listUnassignedQuery}
        onAssign={onAssign}
        onDelete={unassign}
      />
    </Box>
  );
};
